// src/pages/blog/BlogTag.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiCalendar,
  FiClock,
  FiEye,
  FiBookOpen,
  FiChevronLeft,
  FiChevronRight,
  FiTag,
  FiRss,
} from 'react-icons/fi';
import { useBlog } from '../../hooks/useBlog';

// =============================================
// SUB-COMPONENTS
// =============================================

// Blog Card Component
const BlogCard = ({ blog }) => {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
    >
      <Link
        to={`/blog/${blog.slug}`}
        className="group block rounded-xl overflow-hidden border transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
        style={{ borderColor: 'var(--border)' }}
      >
        {blog.featuredImage && (
          <div className="relative h-48 overflow-hidden bg-gray-100 dark:bg-gray-800">
            <img
              src={blog.featuredImage}
              alt={blog.imageAlt || blog.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          </div>
        )}
        <div className="p-5">
          <div
            className="flex items-center gap-3 text-xs mb-3"
            style={{ color: 'var(--muted)' }}
          >
            <span
              className="px-2 py-1 rounded-full"
              style={{ backgroundColor: 'var(--border-light)' }}
            >
              {blog.category}
            </span>
            <div className="flex items-center gap-1">
              <FiCalendar className="w-3 h-3" />
              <span>
                {blog.publishedAt
                  ? new Date(blog.publishedAt).toLocaleDateString()
                  : 'Draft'}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <FiClock className="w-3 h-3" />
              <span>{blog.readingTime || 5} min</span>
            </div>
          </div>
          <h3
            className="text-xl font-semibold mb-2 line-clamp-2 group-hover:underline"
            style={{ color: 'var(--text)' }}
          >
            {blog.title}
          </h3>
          <p
            className="text-sm line-clamp-2 mb-3"
            style={{ color: 'var(--muted)' }}
          >
            {blog.excerpt}
          </p>
          <div className="flex items-center justify-between">
            <div
              className="flex items-center gap-1 text-xs"
              style={{ color: 'var(--muted)' }}
            >
              <FiEye className="w-3 h-3" />
              <span>{blog.views || 0} views</span>
            </div>
            <span
              className="text-sm font-medium group-hover:underline"
              style={{ color: 'var(--text)' }}
            >
              Read More →
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

// Blog Grid Component
const BlogGrid = ({ blogs }) => {
  if (!blogs || blogs.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {blogs.map(blog => (
        <BlogCard key={blog.id} blog={blog} />
      ))}
    </div>
  );
};

// Blog Pagination Component
const BlogPagination = ({ currentPage, totalPages, onPageChange }) => {
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  if (totalPages <= 1) return null;

  return (
    <div
      className="flex items-center justify-center gap-2 mt-12 pt-8 border-t"
      style={{ borderColor: 'var(--border)' }}
    >
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="p-2 rounded-lg border transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ borderColor: 'var(--border)' }}
      >
        <FiChevronLeft className="w-4 h-4" />
      </button>

      {getPageNumbers().map((page, index) => (
        <button
          key={index}
          onClick={() => typeof page === 'number' && onPageChange(page)}
          className={`min-w-[40px] h-10 rounded-lg transition-all duration-200 ${
            currentPage === page
              ? 'bg-black text-white dark:bg-white dark:text-black'
              : page === '...'
                ? 'cursor-default'
                : 'border hover:bg-black/5 dark:hover:bg-white/5'
          }`}
          style={{ borderColor: 'var(--border)' }}
          disabled={page === '...'}
        >
          {page}
        </button>
      ))}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="p-2 rounded-lg border transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ borderColor: 'var(--border)' }}
      >
        <FiChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

// Skeleton Loader Component
const SkeletonLoader = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {[1, 2, 3, 4, 5, 6].map(i => (
        <div
          key={i}
          className="rounded-xl overflow-hidden border animate-pulse"
          style={{ borderColor: 'var(--border)' }}
        >
          <div className="h-48 bg-gray-200 dark:bg-gray-800"></div>
          <div className="p-5 space-y-3">
            <div className="flex gap-2">
              <div className="h-6 w-16 bg-gray-200 dark:bg-gray-800 rounded-full"></div>
              <div className="h-6 w-20 bg-gray-200 dark:bg-gray-800 rounded-full"></div>
            </div>
            <div className="h-6 bg-gray-200 dark:bg-gray-800 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-full"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-5/6"></div>
            <div className="flex justify-between">
              <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-16"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-20"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Subtle Loading Overlay
const LoadingOverlay = () => {
  return (
    <div className="absolute inset-0 bg-white/30 dark:bg-black/30 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl">
      <div
        className="flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-gray-800 shadow-lg border"
        style={{ borderColor: 'var(--border)' }}
      >
        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
        <span className="text-sm" style={{ color: 'var(--text)' }}>
          Loading...
        </span>
      </div>
    </div>
  );
};

// =============================================
// MAIN BLOG TAG COMPONENT
// =============================================

const BlogTag = () => {
  const { tag } = useParams();
  const navigate = useNavigate();
  const { blogs, loading, pagination, fetchBlogs } = useBlog();
  const [error, setError] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const loadBlogs = useCallback(
    async (page = 1) => {
      if (!tag) return;

      setError(null);
      try {
        await fetchBlogs({
          page,
          tag: decodeURIComponent(tag),
          limit: 9,
        });
      } catch (err) {
        console.error('Failed to load tag blogs:', err);
        setError(err.message);
      } finally {
        setIsInitialLoad(false);
      }
    },
    [tag, fetchBlogs],
  );

  useEffect(() => {
    setIsInitialLoad(true);
    loadBlogs(1);
  }, [tag]);

  const handlePageChange = page => {
    loadBlogs(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const decodedTag = decodeURIComponent(tag || '');
  const showLoadingOverlay = loading && !isInitialLoad && blogs.length > 0;

  // Generate tag schema
  const tagSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `#${decodedTag} - Tag | WD Hajbul Blog`,
    description: `Browse all blog posts tagged with ${decodedTag}. Discover articles and insights about ${decodedTag} from WD Hajbul.`,
    url: `https://wdhejbul.com/blog/tag/${tag}`,
    about: {
      '@type': 'Thing',
      name: decodedTag,
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: blogs.slice(0, 10).map((blog, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://wdhejbul.com/blog/${blog.slug}`,
        name: blog.title,
      })),
    },
    publisher: {
      '@type': 'Organization',
      name: 'WD Hajbul Creative Studio',
      logo: {
        '@type': 'ImageObject',
        url: 'https://wdhejbul.com/logo.png',
      },
    },
  };

  if (isInitialLoad && loading) {
    return (
      <>
        <Helmet>
          <title>#{decodedTag} | Tag | WD Hajbul Blog</title>
          <meta
            name="description"
            content={`Browse all blog posts tagged with ${decodedTag}.`}
          />
          <meta name="robots" content="index, follow" />
        </Helmet>
        <div className="min-h-screen pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <button
                onClick={() => navigate('/blog')}
                className="mb-4 text-sm hover:underline flex items-center gap-1"
                style={{ color: 'var(--muted)' }}
              >
                ← Back to Blog
              </button>
              <div className="h-10 w-64 bg-gray-200 dark:bg-gray-800 rounded animate-pulse mb-2"></div>
              <div className="h-5 w-32 bg-gray-200 dark:bg-gray-800 rounded animate-pulse"></div>
            </div>
            <SkeletonLoader />
          </div>
        </div>
      </>
    );
  }

  if (error && blogs.length === 0) {
    return (
      <>
        <Helmet>
          <title>Error | WD Hajbul Blog</title>
        </Helmet>
        <div className="min-h-screen pt-32 pb-20 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
              <FiBookOpen className="w-10 h-10 text-red-500" />
            </div>
            <h2
              className="text-xl font-semibold mb-2"
              style={{ color: 'var(--text)' }}
            >
              Error Loading Posts
            </h2>
            <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>
              {error}
            </p>
            <button
              onClick={() => {
                setError(null);
                loadBlogs(1);
              }}
              className="px-4 py-2 rounded-lg border hover:bg-black/5 dark:hover:bg-white/5 transition-all"
              style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
            >
              Try Again
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>#{decodedTag} | Tag | WD Hajbul Blog</title>
        <meta
          name="description"
          content={`Browse all blog posts tagged with ${decodedTag}. Discover articles and insights about ${decodedTag} from WD Hajbul.`}
        />
        <meta
          name="keywords"
          content={`${decodedTag}, blog, tag, posts, articles, ${decodedTag} insights, design, development`}
        />
        <link rel="canonical" href={`https://wdhejbul.com/blog/tag/${tag}`} />

        {/* Open Graph */}
        <meta
          property="og:title"
          content={`#${decodedTag} | Tag | WD Hajbul Blog`}
        />
        <meta
          property="og:description"
          content={`Browse all blog posts tagged with ${decodedTag}.`}
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`https://wdhejbul.com/blog/tag/${tag}`}
        />
        <meta property="og:site_name" content="WD Hajbul Creative Studio" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`#${decodedTag} | Tag | WD Hajbul Blog`}
        />
        <meta
          name="twitter:description"
          content={`Browse all blog posts tagged with ${decodedTag}.`}
        />

        {/* Robots */}
        <meta name="robots" content="index, follow" />

        {/* Schema */}
        <script type="application/ld+json">{JSON.stringify(tagSchema)}</script>
      </Helmet>

      <div className="min-h-screen pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <button
              onClick={() => navigate('/blog')}
              className="mb-4 text-sm hover:underline flex items-center gap-1 transition-colors"
              style={{ color: 'var(--muted)' }}
            >
              ← Back to Blog
            </button>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <FiTag className="w-6 h-6" style={{ color: 'var(--text)' }} />
                <div>
                  <h1
                    className="text-3xl md:text-4xl font-bold"
                    style={{ color: 'var(--text)' }}
                  >
                    Tag: #{decodedTag}
                  </h1>
                  <p className="mt-2" style={{ color: 'var(--muted)' }}>
                    {pagination.totalItems} post
                    {pagination.totalItems !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <Link
                to="/blog/rss.xml"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-all hover:gap-3"
                style={{
                  backgroundColor: 'var(--border-light)',
                  color: 'var(--text)',
                }}
              >
                <FiRss className="w-3.5 h-3.5 text-orange-500" />
                RSS Feed
              </Link>
            </div>
          </div>

          <div className="relative">
            {showLoadingOverlay && <LoadingOverlay />}

            {blogs.length === 0 && !loading ? (
              <div className="text-center py-20">
                <div
                  className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6"
                  style={{ backgroundColor: 'var(--border-light)' }}
                >
                  <FiTag
                    className="w-10 h-10"
                    style={{ color: 'var(--muted)' }}
                  />
                </div>
                <h3
                  className="text-xl font-semibold mb-2"
                  style={{ color: 'var(--text)' }}
                >
                  No posts found
                </h3>
                <p className="text-base" style={{ color: 'var(--muted)' }}>
                  No blog posts found with the tag "#{decodedTag}".
                </p>
                <button
                  onClick={() => navigate('/blog')}
                  className="mt-4 px-4 py-2 rounded-lg border hover:bg-black/5 dark:hover:bg-white/5 transition-all"
                  style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                >
                  View all posts
                </button>
              </div>
            ) : (
              <>
                <BlogGrid blogs={blogs} />
                {pagination.totalPages > 1 && !loading && (
                  <BlogPagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    onPageChange={handlePageChange}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogTag;
